
import {
  computed, defineComponent, PropType, ref, watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Textarea from 'primevue/textarea';
import Message from 'primevue/message';
import moment from 'moment';
import WizardDialog from '@/components/billing/shared/WizardDialog.vue';
import { cancelPlan } from '@/composables/billing/billingApi';
import { useStructuralReload } from '@/composables/billing/useStructuralReload';

const PHASES = ['confirm'] as const;
const PHASE = 'confirm';

/**
 * Subscription cancellation modal. Single-step but rendered through
 * {@link WizardDialog} so the chrome (header / subtitle / close button)
 * matches the rest of the billing modals (BuyCredits, ChangePlan,
 * MigrateV5). Phases array has one entry — the progress bar is hidden
 * by WizardDialog when `phases.length <= 1`.
 *
 * Visual choices :
 *   - Top card highlights the « active until » date (the only piece of
 *     info the user really needs to make the decision).
 *   - Reason picker uses card-style buttons instead of stock RadioButtons
 *     for a faster click target and consistent hover feedback.
 *   - In-flow footer with ghost + danger CTAs, no PrimeVue `<template
 *     #footer>` — keeps spacing aligned with the rest of the phase.
 */
export default defineComponent({
  name: 'UnsubscribeDialog',

  components: { WizardDialog, Textarea, Message },

  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    effectiveAt: {
      type: String as PropType<string | null>,
      default: null,
    },
  },

  emits: ['update:visible', 'cancelled'],

  setup(props, { emit }) {
    const { t } = useI18n();
    const handleReload = useStructuralReload();

    const reason = ref<string>('too_expensive');
    const comment = ref('');
    const submitting = ref(false);
    const errorMessage = ref<string | null>(null);

    const reasonOptions = computed(() => [
      {
        value: 'too_expensive',
        label: t('billing.unsubscribe.reasons.tooExpensive'),
        icon: 'fa-regular fa-tag',
      },
      {
        value: 'closing_shop',
        label: t('billing.unsubscribe.reasons.closingShop'),
        icon: 'fa-regular fa-shop-slash',
      },
      {
        value: 'competitor',
        label: t('billing.unsubscribe.reasons.competitor'),
        icon: 'fa-regular fa-arrows-turn-to-dots',
      },
      {
        value: 'not_using_enough',
        label: t('billing.unsubscribe.reasons.notUsingEnough'),
        icon: 'fa-regular fa-chart-line-down',
      },
      {
        value: 'other',
        label: t('billing.unsubscribe.reasons.other'),
        icon: 'fa-regular fa-comment-dots',
      },
    ]);

    const formattedEffectiveAt = computed(() => (
      props.effectiveAt ? moment(props.effectiveAt).format('DD/MM/YYYY') : '—'
    ));

    const titles = computed(() => ({
      confirm: t('billing.unsubscribe.title'),
    }));
    const subtitles = computed(() => ({
      confirm: t('billing.unsubscribe.subtitle'),
    }));

    const reset = () => {
      reason.value = 'too_expensive';
      comment.value = '';
      errorMessage.value = null;
    };

    const onVisibleChange = (v: boolean) => {
      if (!v) onClose();
    };

    const onClose = () => {
      if (submitting.value) return;
      emit('update:visible', false);
      setTimeout(reset, 220);
    };

    const onConfirm = async () => {
      submitting.value = true;
      errorMessage.value = null;
      try {
        const result = await cancelPlan({
          reason: reason.value,
          comment: comment.value || undefined,
        });
        await handleReload(result, {
          onFailure: (err) => {
            errorMessage.value = err;
          },
        });
        if (result.status === 'completed') {
          emit('cancelled');
          emit('update:visible', false);
          setTimeout(reset, 220);
        }
      } finally {
        submitting.value = false;
      }
    };

    watch(() => props.visible, (v) => { if (v) reset(); });

    return {
      t,
      PHASE,
      PHASES,
      reason,
      comment,
      submitting,
      errorMessage,
      reasonOptions,
      formattedEffectiveAt,
      titles,
      subtitles,
      onVisibleChange,
      onClose,
      onConfirm,
    };
  },
});
